import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { LoggerService } from '../utils/logger.service';

export interface AdminActivityDay {
  date: string;
  totalNonAdminUsers: number;
  activeSessionsCount: number;
}

export interface SocialAnalyticsSummary {
  from: string;
  to: string;
  courseId?: number | null;
  dailyActiveLearners: number;
  trainNextShown: number;
  trainNextAccepted: number;
  trainNextAcceptanceRate: number;
  remindersSent: number;
  remindersOpened: number;
  remindersConverted: number;
  reminderOpenRate: number;
  reminderConvertRate: number;
  pendingFriendRequests: number;
  acceptedFriendships: number;
  testSharesInPeriod: number;
  streakMilestones7: number;
  streakMilestones14: number;
  streakMilestones30: number;
}

export interface NotificationTemplateRow {
  id: number;
  triggerKey: string;
  channel: string;
  headline: string;
  body: string;
  active: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface AntifraudSummary {
  open: number;
  openAccountSharing: number;
  openContentScrape: number;
  openRobotRequest: number;
}

export interface AntifraudEventRow {
  id: number;
  userId: number;
  username?: string | null;
  signal: string;
  severity: string;
  detail?: string | null;
  ipAddress?: string | null;
  deviceId?: string | null;
  userAgent?: string | null;
  path?: string | null;
  createdAt: string;
  status: string;
  resolvedAt?: string | null;
}

/**
 * Admin-only REST client. Imported exclusively by admin/* components, which are
 * excluded from the Capacitor bundle via app.routes.capacitor.ts — esbuild
 * tree-shakes this entire service out of the mobile APK.
 */
@Injectable({ providedIn: 'root' })
export class AdminRestService {
  constructor(
    private http: HttpClient,
    private runtime: RuntimeConfigService,
  ) {}

  private get apiUrl(): string {
    return this.runtime.apiBaseUrl;
  }

  private getAuthHeaders(sessionId: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${sessionId}`,
      'Content-Type': 'application/json',
    });
  }

  /** Admin: dashboard stats */
  async getAdminStats(sessionId: string): Promise<{ totalUsers: number; activeSessions: number; contactMessagesUnread: number; testsLast30Days: number; openFraudEvents?: number } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<{ totalUsers: number; activeSessions: number; contactMessagesUnread: number; testsLast30Days: number; openFraudEvents?: number }>(`${this.apiUrl}/admin/stats`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to get admin stats:', error);
      return null;
    }
  }

  /** Admin: activity time series for chart (last 30 days) */
  async getAdminActivity(sessionId: string): Promise<{ activityTimeSeries: AdminActivityDay[] } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<{ activityTimeSeries: AdminActivityDay[] }>(`${this.apiUrl}/admin/stats/activity`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to get admin activity:', error);
      return null;
    }
  }

  /** Admin: list global settings */
  async getAdminSettings(sessionId: string): Promise<{ key: string; value: string | null; valueNum: number | null }[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(this.http.get<any[]>(`${this.apiUrl}/admin/settings`, { headers }));
    } catch (error) {
      LoggerService.error('Failed to get admin settings:', error);
      return null;
    }
  }

  /** Admin: update global settings (map key -> value or valueNum) */
  async putAdminSettings(sessionId: string, body: Record<string, string | number>): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(this.http.put(`${this.apiUrl}/admin/settings`, body, { headers }));
      return true;
    } catch (error) {
      LoggerService.error('Failed to update admin settings:', error);
      return false;
    }
  }

  /** Admin: list users (paginated; sort e.g. 'username,asc'; adminOnly for filter) */
  async getAdminUsers(sessionId: string, page = 0, size = 20, adminOnly?: boolean, sort?: string): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    let url = `${this.apiUrl}/admin/users?page=${page}&size=${size}`;
    if (adminOnly !== undefined) url += `&adminOnly=${adminOnly}`;
    if (sort) url += `&sort=${encodeURIComponent(sort)}`;
    return await firstValueFrom(this.http.get<any>(url, { headers }));
  }

  /** Admin: create user (username + password required) */
  async postAdminUser(sessionId: string, body: Record<string, unknown>): Promise<{ id: number } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const res = await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/users`, body, { headers }));
      return res != null ? { id: res.id } : null;
    } catch (error) {
      LoggerService.error('Failed to create user:', error);
      return null;
    }
  }

  /** Admin: get single user */
  async getAdminUser(sessionId: string, id: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/users/${id}`, { headers }));
  }

  /** Admin: update user */
  async putAdminUser(sessionId: string, id: number, body: Record<string, unknown>): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(this.http.put(`${this.apiUrl}/admin/users/${id}`, body, { headers }));
      return true;
    } catch (error) {
      LoggerService.error('Failed to update user:', error);
      return false;
    }
  }

  /** Admin: delete user */
  async deleteAdminUser(sessionId: string, id: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(this.http.delete(`${this.apiUrl}/admin/users/${id}`, { headers }));
      return true;
    } catch (error) {
      LoggerService.error('Failed to delete user:', error);
      return false;
    }
  }

  /** Admin: list subscription definitions */
  async getAdminSubscriptions(sessionId: string): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(this.http.get<any[]>(`${this.apiUrl}/admin/subscriptions`, { headers }));
    } catch (error) {
      LoggerService.error('Failed to get admin subscriptions:', error);
      return null;
    }
  }

  /** Admin: create subscription definition */
  async postAdminSubscription(sessionId: string, body: { name: string; courseId?: number | null; subjectId?: number | null; revenuecatEntitlementId?: string | null; revenuecatOfferingId?: string | null; active?: number | null }): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/subscriptions`, body, { headers }));
  }

  /** Admin: update subscription definition */
  async putAdminSubscription(sessionId: string, id: number, body: { name?: string; courseId?: number | null; subjectId?: number | null; revenuecatEntitlementId?: string | null; revenuecatOfferingId?: string | null; active?: number | null }): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/subscriptions/${id}`, body, { headers }));
  }

  async getAdminTrialPolicy(sessionId: string, subscriptionId: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/subscriptions/${subscriptionId}/trial-policy`, { headers }));
  }

  async putAdminTrialPolicy(sessionId: string, subscriptionId: number, body: any): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/subscriptions/${subscriptionId}/trial-policy`, body, { headers }));
  }

  /** Admin: delete subscription definition */
  async deleteAdminSubscription(sessionId: string, id: number): Promise<void> {
    const headers = this.getAuthHeaders(sessionId);
    await firstValueFrom(this.http.delete(`${this.apiUrl}/admin/subscriptions/${id}`, { headers }));
  }

  /** Admin: list user subscriptions (filter by userId) */
  async getAdminUserSubscriptions(sessionId: string, userId: number, page = 0, size = 50): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(
      this.http.get<any>(`${this.apiUrl}/admin/user-subscriptions?page=${page}&size=${size}&userId=${userId}`, { headers })
    );
  }

  /** Admin: grant user subscription */
  async postAdminUserSubscription(sessionId: string, body: { userId: number; subscriptionId: number; validFrom: string; validTo: string; transactionId?: string }): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(this.http.post(`${this.apiUrl}/admin/user-subscriptions`, body, { headers }));
      return true;
    } catch (error) {
      LoggerService.error('Failed to grant user subscription:', error);
      return false;
    }
  }

  /** Admin: revoke user subscription */
  async deleteAdminUserSubscription(sessionId: string, id: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(this.http.delete(`${this.apiUrl}/admin/user-subscriptions/${id}`, { headers }));
      return true;
    } catch (error) {
      LoggerService.error('Failed to revoke user subscription:', error);
      return false;
    }
  }

  /** Admin: list contact messages (paginated, sortable, filterable) */
  async getAdminContactMessages(sessionId: string, page = 0, size = 100, sort = 'createdAt,desc', inbox?: boolean, flag?: string, unread?: boolean): Promise<any> {
    let url = `${this.apiUrl}/admin/contact-messages?page=${page}&size=${size}&sort=${encodeURIComponent(sort)}`;
    if (inbox !== undefined) url += `&inbox=${inbox}`;
    if (flag) url += `&flag=${encodeURIComponent(flag)}`;
    if (unread !== undefined) url += `&unread=${unread}`;
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(url, { headers }));
  }

  /** Admin: get single contact message */
  async getAdminContactMessage(sessionId: string, id: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/contact-messages/${id}`, { headers }));
  }

  /** Admin: get full thread for a message */
  async getAdminContactMessageThread(sessionId: string, id: number): Promise<any[]> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any[]>(`${this.apiUrl}/admin/contact-messages/${id}/thread`, { headers }));
  }

  /** Admin: download contact message attachment */
  async getAdminContactMessageAttachment(sessionId: string, id: number): Promise<Blob> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get(`${this.apiUrl}/admin/contact-messages/${id}/attachment`, { headers, responseType: 'blob' }));
  }

  /** Admin: delete contact message (soft or hard; inbox=true when admin is receiver) */
  async deleteAdminContactMessage(sessionId: string, id: number, inbox: boolean): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(this.http.delete(`${this.apiUrl}/admin/contact-messages/${id}?inbox=${inbox}`, { headers }));
      return true;
    } catch (error) {
      LoggerService.error('Failed to delete contact message', error);
      return false;
    }
  }

  /** Admin: update contact message (flag, etc.) */
  async patchAdminContactMessage(sessionId: string, id: number, body: { receiverFlag?: string; receiverDeleted?: number }): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.patch<any>(`${this.apiUrl}/admin/contact-messages/${id}`, body, { headers }));
  }

  /** Admin: send reply to contact message (multipart if attachment) */
  async replyAdminContactMessage(sessionId: string, id: number, replyText: string, attachment?: File): Promise<any> {
    const formData = new FormData();
    formData.append('replyText', replyText);
    if (attachment) formData.append('attachment', attachment);
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${sessionId}` });
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/contact-messages/${id}/reply`, formData, { headers }));
  }

  /** Admin: get contact messages config (max attachment size) */
  async getAdminContactConfig(sessionId: string): Promise<{ maxAttachmentSizeMb: number } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(this.http.get<{ maxAttachmentSizeMb: number }>(`${this.apiUrl}/admin/contact-messages/config`, { headers }));
    } catch (error) {
      LoggerService.error('Failed to get contact config', error);
      return null;
    }
  }

  /** Admin: search users for recipient */
  async searchAdminUsers(sessionId: string, q: string): Promise<{ id: number; username: string; firstName: string; lastName: string; displayName: string }[]> {
    if (!q || q.length < 2) return [];
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any[]>(`${this.apiUrl}/admin/contact-messages/user-search?q=${encodeURIComponent(q)}`, { headers }));
  }

  /** Admin: create new message (multipart) - do NOT set Content-Type; browser sets it with boundary */
  async createAdminContactMessage(sessionId: string, recipientUserId: number, topic: string, messageText: string, attachment?: File): Promise<any> {
    const formData = new FormData();
    formData.append('recipientUserId', String(recipientUserId));
    formData.append('topic', topic);
    formData.append('messageText', messageText);
    if (attachment) formData.append('attachment', attachment);
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${sessionId}` });
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/contact-messages`, formData, { headers }));
  }
  /** Admin: get question comments; userId null = "All" (send 0), else specific user. courseId required when "All". */
  async getAdminQuestionComments(sessionId: string, userId: number | null, courseId?: number, subjectId?: number): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const cid = courseId !== undefined && courseId != null ? Number(courseId) : undefined;
      const sid = subjectId !== undefined && subjectId != null ? Number(subjectId) : undefined;
      const params: string[] = [];
      if (userId != null && userId > 0) {
        params.push(`userId=${Number(userId)}`);
      } else {
        params.push('userId=0');
      }
      if (cid != null) params.push(`courseId=${cid}`);
      if (sid != null) params.push(`subjectId=${sid}`);
      const url = `${this.apiUrl}/admin/question-comments?${params.join('&')}`;
      const response = await firstValueFrom(this.http.get<any[]>(url, { headers }));
      return Array.isArray(response) ? response : (response ? [response] : []);
    } catch (error) {
      LoggerService.error('Failed to load admin question comments:', error);
      return null;
    }
  }
  /** Admin: get all courses (list returns { content: Course[] }); optional seedId filter */
  async getAdminCourses(sessionId: string, activeOnly?: boolean, seedId?: number): Promise<{ id: number; name: string; seedId?: number; active?: number; createdAt?: string }[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      let url = `${this.apiUrl}/admin/courses`;
      const params: string[] = [];
      if (activeOnly !== undefined) params.push(`activeOnly=${activeOnly}`);
      if (seedId != null) params.push(`seedId=${seedId}`);
      if (params.length) url += '?' + params.join('&');
      const response = await firstValueFrom(this.http.get<any>(url, { headers }));
      const arr = response?.content ?? [];
      if (!Array.isArray(arr)) return [];
      return arr.map((c: any) => ({
        id: Number(c.id ?? 0),
        name: String(c.name ?? ''),
        seedId: c.seedId != null ? Number(c.seedId) : undefined,
        active: c.active != null ? Number(c.active) : undefined,
        createdAt: c.createdAt
      })).filter((c: { id: number }) => c.id > 0);
    } catch (error) {
      LoggerService.error('Failed to load admin courses:', error);
      return null;
    }
  }

  /** Admin: get subjects (list returns { content: Subject[] }); optional courseId and seedId filter */
  async getAdminSubjects(sessionId: string, courseId?: number, seedId?: number): Promise<{ id: number; name: string; courseId: number; seedId?: number; active?: number; examQuestions?: number; examTimeMinutes?: number }[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      let url = `${this.apiUrl}/admin/subjects`;
      const params: string[] = [];
      if (courseId != null) params.push(`courseId=${courseId}`);
      if (seedId != null) params.push(`seedId=${seedId}`);
      if (params.length) url += '?' + params.join('&');
      const response = await firstValueFrom(this.http.get<any>(url, { headers }));
      const arr = response?.content ?? [];
      if (!Array.isArray(arr)) return [];
      return arr.map((s: any) => ({
        id: Number(s.id ?? 0),
        name: String(s.name ?? ''),
        courseId: Number(s.courseId ?? courseId ?? 0),
        seedId: s.seedId != null ? Number(s.seedId) : undefined,
        active: s.active != null ? Number(s.active) : undefined,
        examQuestions: s.examQuestions != null ? Number(s.examQuestions) : undefined,
        examTimeMinutes: s.examTimeMinutes != null ? Number(s.examTimeMinutes) : undefined
      })).filter((s: { id: number }) => s.id > 0);
    } catch (error) {
      LoggerService.error('Failed to load admin subjects:', error);
      return null;
    }
  }

  /** Admin: list courses for dropdowns (id, name); optional seedId to filter by seed */
  async getAdminCoursesList(sessionId: string, seedId?: number): Promise<{ id: number; name: string }[] | null> {
    const list = await this.getAdminCourses(sessionId, undefined, seedId);
    return list?.map(c => ({ id: c.id, name: c.name })) ?? null;
  }

  /** Admin: list subjects for a course for dropdowns */
  async getAdminSubjectsList(sessionId: string, courseId: number, seedId?: number): Promise<{ id: number; name: string; courseId: number }[] | null> {
    const list = await this.getAdminSubjects(sessionId, courseId, seedId);
    return list?.map(s => ({ id: s.id, name: s.name, courseId: s.courseId })) ?? null;
  }

  /** Admin: get one course */
  async getAdminCourse(sessionId: string, id: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/courses/${id}`, { headers }));
  }

  /** Admin: create course */
  async postAdminCourse(sessionId: string, body: { name: string; seedId?: number; active?: number }): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/courses`, body, { headers }));
  }

  /** Admin: update course */
  async putAdminCourse(sessionId: string, id: number, body: Record<string, unknown>): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/courses/${id}`, body, { headers }));
  }

  /** Admin: delete course (409 if dependencies) */
  async deleteAdminCourse(sessionId: string, id: number): Promise<{ error?: string } | void> {
    const headers = this.getAuthHeaders(sessionId);
    const res = await firstValueFrom(this.http.delete<any>(`${this.apiUrl}/admin/courses/${id}`, { headers }));
    return res;
  }

  /** Admin: get one subject */
  async getAdminSubject(sessionId: string, id: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/subjects/${id}`, { headers }));
  }

  /** Admin: create subject */
  async postAdminSubject(sessionId: string, body: { courseId: number; name: string; seedId?: number; active?: number; examQuestions?: number; examTimeMinutes?: number }): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/subjects`, body, { headers }));
  }

  /** Admin: update subject */
  async putAdminSubject(sessionId: string, id: number, body: Record<string, unknown>): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/subjects/${id}`, body, { headers }));
  }

  /** Admin: delete subject (409 if dependencies) */
  async deleteAdminSubject(sessionId: string, id: number): Promise<{ error?: string } | void> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.delete<any>(`${this.apiUrl}/admin/subjects/${id}`, { headers }));
  }

  /** Admin: list question seeds */
  async getAdminSeeds(sessionId: string): Promise<{ id: number; name: string; createdAt?: string }[]> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const raw = await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/seeds`, { headers }));
      let list: any[] = Array.isArray(raw) ? raw : (raw?.content ?? raw?.data ?? []);
      if (!Array.isArray(list) || list.length === 0) {
        if (raw != null && typeof raw === 'object' && (raw.id != null || raw.name != null))
          list = [raw];
        else
          return [];
      }
      return list.map((s: any) => ({ id: Number(s?.id ?? 0), name: String(s?.name ?? ''), createdAt: s?.createdAt })).filter((s: { id: number }) => s.id > 0);
    } catch (error) {
      LoggerService.error('Failed to load admin seeds:', error);
      return [];
    }
  }

  /** Admin: get default (latest) seed for admin preselection */
  async getAdminSeedDefault(sessionId: string): Promise<{ id: number; name: string; createdAt?: string } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const seed = await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/seeds/default`, { headers }));
      return seed ? { id: Number(seed.id ?? 0), name: String(seed.name ?? ''), createdAt: seed.createdAt } : null;
    } catch (error) {
      LoggerService.error('Failed to load admin default seed:', error);
      return null;
    }
  }

  /** Admin: list external providers */
  async getAdminExternalProviders(sessionId: string): Promise<{ id: number; name: string; url?: string }[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const list = await firstValueFrom(this.http.get<any[]>(`${this.apiUrl}/admin/external-providers`, { headers }));
      return Array.isArray(list) ? list : null;
    } catch (error) {
      LoggerService.error('Failed to load admin external providers:', error);
      return null;
    }
  }

  /** Admin: list topics */
  async getAdminTopics(sessionId: string, subjectId?: number, seedId?: number): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      let url = `${this.apiUrl}/admin/topics`;
      const params: string[] = [];
      if (subjectId != null) params.push(`subjectId=${subjectId}`);
      if (seedId != null) params.push(`seedId=${seedId}`);
      if (params.length) url += '?' + params.join('&');
      const response = await firstValueFrom(this.http.get<any>(url, { headers }));
      const arr = response?.content ?? [];
      return Array.isArray(arr) ? arr : null;
    } catch (error) {
      LoggerService.error('Failed to load admin topics:', error);
      return null;
    }
  }

  /** Admin: get one topic */
  async getAdminTopic(sessionId: string, id: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/topics/${id}`, { headers }));
  }

  /** Admin: create topic */
  async postAdminTopic(sessionId: string, body: { subjectId: number; name: string; seedId?: number }): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/topics`, body, { headers }));
  }

  /** Admin: update topic */
  async putAdminTopic(sessionId: string, id: number, body: Record<string, unknown>): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/topics/${id}`, body, { headers }));
  }

  /** Admin: delete topic (409 if dependencies) */
  async deleteAdminTopic(sessionId: string, id: number): Promise<{ error?: string } | void> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.delete<any>(`${this.apiUrl}/admin/topics/${id}`, { headers }));
  }

  /** Admin: list news */
  async getAdminNews(sessionId: string): Promise<{ id: number; headline: string; text: string; active: number; createdAt: string }[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/news`, { headers }));
      const arr = response?.content ?? [];
      return Array.isArray(arr) ? arr : null;
    } catch (error) {
      LoggerService.error('Failed to load admin news:', error);
      return null;
    }
  }

  /** Admin: create news */
  async postAdminNews(sessionId: string, body: { headline: string; text: string; active: number }): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/news`, body, { headers }));
  }

  /** Admin: update news */
  async putAdminNews(sessionId: string, id: number, body: Record<string, unknown>): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/news/${id}`, body, { headers }));
  }

  /** Admin: delete news */
  async deleteAdminNews(sessionId: string, id: number): Promise<void> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.delete<any>(`${this.apiUrl}/admin/news/${id}`, { headers }));
  }

  /** Admin: social analytics aggregates */
  async getAdminSocialAnalytics(
    sessionId: string,
    params: { from?: string; to?: string; courseId?: number }
  ): Promise<SocialAnalyticsSummary | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const query: string[] = [];
      if (params.from) query.push(`from=${encodeURIComponent(params.from)}`);
      if (params.to) query.push(`to=${encodeURIComponent(params.to)}`);
      if (params.courseId != null) query.push(`courseId=${params.courseId}`);
      const suffix = query.length ? `?${query.join('&')}` : '';
      return await firstValueFrom(
        this.http.get<SocialAnalyticsSummary>(`${this.apiUrl}/admin/social/analytics${suffix}`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to load social analytics:', error);
      throw error;
    }
  }

  /** Admin: list notification templates */
  async getAdminSocialTemplates(sessionId: string): Promise<NotificationTemplateRow[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/social/templates`, { headers }));
      const arr = response?.content ?? [];
      return Array.isArray(arr) ? arr : null;
    } catch (error) {
      LoggerService.error('Failed to load notification templates:', error);
      return null;
    }
  }

  async postAdminSocialTemplate(
    sessionId: string,
    body: { triggerKey: string; channel: string; headline: string; body: string; active: number }
  ): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/social/templates`, body, { headers }));
  }

  async putAdminSocialTemplate(sessionId: string, id: number, body: Record<string, unknown>): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/social/templates/${id}`, body, { headers }));
  }

  async deleteAdminSocialTemplate(sessionId: string, id: number): Promise<void> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.delete<any>(`${this.apiUrl}/admin/social/templates/${id}`, { headers }));
  }

  /** Admin: list areas */
  async getAdminAreas(sessionId: string, topicId?: number, seedId?: number): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      let url = `${this.apiUrl}/admin/areas`;
      const params: string[] = [];
      if (topicId != null) params.push(`topicId=${topicId}`);
      if (seedId != null) params.push(`seedId=${seedId}`);
      if (params.length) url += '?' + params.join('&');
      const response = await firstValueFrom(this.http.get<any>(url, { headers }));
      const arr = response?.content ?? [];
      return Array.isArray(arr) ? arr : null;
    } catch (error) {
      LoggerService.error('Failed to load admin areas:', error);
      return null;
    }
  }

  /** Admin: get one area */
  async getAdminArea(sessionId: string, id: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/areas/${id}`, { headers }));
  }

  /** Admin: create area */
  async postAdminArea(sessionId: string, body: { topicId: number; name: string; seedId?: number }): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/areas`, body, { headers }));
  }

  /** Admin: update area */
  async putAdminArea(sessionId: string, id: number, body: Record<string, unknown>): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/areas/${id}`, body, { headers }));
  }

  /** Admin: delete area (409 if dependencies) */
  async deleteAdminArea(sessionId: string, id: number): Promise<{ error?: string } | void> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.delete<any>(`${this.apiUrl}/admin/areas/${id}`, { headers }));
  }

  /** Admin: list questions (paginated; filter by courseId, subjectId, topicId, areaId, seedId; sort e.g. id,desc) */
  async getAdminQuestions(
    sessionId: string,
    params?: { courseId?: number; subjectId?: number; topicId?: number; areaId?: number; seedId?: number; includeRemoved?: boolean; sort?: string; page?: number; size?: number }
  ): Promise<{ content: any[]; totalElements: number; totalPages: number; number: number; size: number; first: boolean; last: boolean } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      let url = `${this.apiUrl}/admin/questions`;
      const query: string[] = [];
      if (params?.courseId != null) query.push(`courseId=${params.courseId}`);
      if (params?.subjectId != null) query.push(`subjectId=${params.subjectId}`);
      if (params?.topicId != null) query.push(`topicId=${params.topicId}`);
      if (params?.areaId != null) query.push(`areaId=${params.areaId}`);
      if (params?.seedId != null) query.push(`seedId=${params.seedId}`);
      if (params?.includeRemoved === true) query.push('includeRemoved=true');
      if (params?.sort != null && params.sort !== '') query.push(`sort=${encodeURIComponent(params.sort)}`);
      const page = params?.page ?? 0;
      const size = params?.size ?? 20;
      query.push(`page=${page}`);
      query.push(`size=${size}`);
      url += `?${query.join('&')}`;
      const response = await firstValueFrom(this.http.get<any>(url, { headers }));
      const arr = response?.content ?? [];
      return {
        content: Array.isArray(arr) ? arr : [],
        totalElements: response?.totalElements ?? 0,
        totalPages: response?.totalPages ?? 0,
        number: response?.number ?? 0,
        size: response?.size ?? size,
        first: response?.first ?? true,
        last: response?.last ?? true
      };
    } catch (error) {
      LoggerService.error('Failed to load admin questions:', error);
      return null;
    }
  }

  /** Admin: get one question */
  async getAdminQuestion(sessionId: string, id: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any>(`${this.apiUrl}/admin/questions/${id}`, { headers }));
  }

  /** Admin: create question */
  async postAdminQuestion(sessionId: string, body: Record<string, unknown>): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/questions`, body, { headers }));
  }

  /** Admin: update question */
  async putAdminQuestion(sessionId: string, id: number, body: Record<string, unknown>): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/questions/${id}`, body, { headers }));
  }

  /** Admin: soft delete question */
  async postAdminQuestionSoftDelete(sessionId: string, id: number): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/admin/questions/${id}/soft-delete`, {}, { headers }));
  }

  /** Admin: hard delete question (409 if dependencies) */
  async deleteAdminQuestion(sessionId: string, id: number): Promise<{ error?: string } | void> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.delete<any>(`${this.apiUrl}/admin/questions/${id}`, { headers }));
  }

  /** Admin: get users subscribed to a course (id, username), sorted by username */
  async getAdminSubscribedUsers(sessionId: string, courseId: number): Promise<{ id: number; username: string }[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any[]>(`${this.apiUrl}/admin/courses/${courseId}/subscribed-users`, { headers })
      );
      return response || [];
    } catch (error) {
      LoggerService.error('Failed to load subscribed users:', error);
      return null;
    }
  }

  async getAntifraudSummary(sessionId: string): Promise<AntifraudSummary | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<AntifraudSummary>(`${this.apiUrl}/admin/antifraud/summary`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to load antifraud summary:', error);
      return null;
    }
  }

  async getAntifraudEvents(
    sessionId: string,
    page = 0,
    size = 20,
    status?: string,
    signal?: string
  ): Promise<{ content: AntifraudEventRow[]; totalElements: number; totalPages: number; number: number; size: number } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('size', String(size));
      if (status) params.set('status', status);
      if (signal) params.set('signal', signal);
      return await firstValueFrom(
        this.http.get<{ content: AntifraudEventRow[]; totalElements: number; totalPages: number; number: number; size: number }>(
          `${this.apiUrl}/admin/antifraud/events?${params.toString()}`,
          { headers }
        )
      );
    } catch (error) {
      LoggerService.error('Failed to load antifraud events:', error);
      return null;
    }
  }

  async patchAntifraudEvent(sessionId: string, id: number, status: string): Promise<AntifraudEventRow | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.patch<AntifraudEventRow>(`${this.apiUrl}/admin/antifraud/events/${id}`, { status }, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to update antifraud event:', error);
      return null;
    }
  }
}
